import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import HomeService from "../../../Services/HomeService";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

const ManageBasicUser = () => {
  const [allData, setAllData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [filteredInformation, setFilteredInformation] = useState([]);
  const [cities, setCities] = useState([]);

  const userCity = localStorage.getItem("city");

  const initialFormData = {
    email: "",
    fullName: "",
    password: "",
    city: "",
    companyName: "",
    address: "",
    mobileNo: "",
    workExpYear: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [editID, setEditID] = useState("");

  const handleChange = (e) => {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    if (name === "mobileNo") {
      value = value.replace(/\D/g, ""); // Keep only digits
    }

    setFormData({ ...formData, [name]: value });
  };

  const fetchAllData = async () => {
    try {
      const response = await HomeService.getBasicUsers();
      console.log("BasicUsers response", response?.data);

      const formattedData = (response?.data || []).map((item, index) => ({
        id: item._id,
        sln: index + 1,
        cityName: item?.city?.name || "",
        fullName: item?.fullName || "",
        city: item?.city?._id || "",
        email: item?.email || "",
        mobileNo: item?.mobileNo || "",
        workExpYear: item?.workExpYear || "",
        companyName: item?.companyName || "",
        address: item?.address || "",
        password: item?.password || item?.visiblePassword || "",
        action: (
          <>
            <span title="Edit" onClick={() => handleEdit(item)}>
              <i
                className="fa-solid fa-pen-to-square"
                style={{
                  cursor: "pointer",
                  padding: "5px",
                  marginRight: "5px",
                  fontSize: "1.5em",
                  color: "rgb(133, 218, 79)",
                  backgroundColor: "#E9F3F7",
                }}
              ></i>
            </span>

            <span title="Delete" onClick={() => handleDelete(item._id)}>
              <i
                className="fa-solid fa-trash"
                style={{
                  cursor: "pointer",
                  padding: "5px",
                  marginRight: "5px",
                  fontSize: "1.5em",
                  color: " red",
                  backgroundColor: "#E9F3F7",
                }}
              ></i>
            </span>
          </>
        ),
      }));

      if (userCity) {
        setAllData(
          formattedData.filter((item) => item?.city === userCity) || [],
        );
      } else {
        setAllData(formattedData || []);
      }
    } catch (error) {
      console.error("Error fetching Basic Users:", error);
    }
  };

  // Fetch cities
  const fetchCities = async () => {
    try {
      const res = await HomeService.getCities();
      if (res?.data) {
        if (userCity) {
          setCities([res.data.find((item) => item._id === userCity)]);
          setFormData({ ...formData, city: userCity });
        } else {
          setCities(res.data);
        }
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  useEffect(() => {
    fetchAllData();
    fetchCities();
  }, []);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Deleting Basic User...",
      text: "Are you sure you want to delete this Basic User record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "No, not sure!",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        const res = await HomeService.deleteBasicUser(id);
        if (res?.status) {
          Swal.fire("Deleted!", "Basic User record has been deleted.", "success");
          fetchAllData();
        } else {
          Swal.fire("Error!", res?.message || "Failed to delete Basic User record.", "error");
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to delete Basic User record.", "error");
      }
    }
  };

  const handleEdit = (data) => {
    setEditID(data?._id || data?.id || "");
    setFormData({
      email: data.email || "",
      fullName: data.fullName || "",
      password: data.password || data.visiblePassword || "",
      city: data.city?._id || data.city || "",
      companyName: data.companyName || "",
      address: data.address || "",
      mobileNo: data.mobileNo || "",
      workExpYear: data.workExpYear || "",
    });

    const editSection = document.getElementById("edit-section-basic");
    if (editSection) {
      editSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData?.mobileNo?.toString()?.length !== 10) {
      toast.error("Mobile number should be in 10 digits");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const res = await HomeService.updateBasicUser(editID, formData);
      if (res?.status) {
        fetchAllData();
        setEditID(null);
        toast.success("Basic User updated successfully!");
      } else {
        toast.error(res?.message || "Failed to update Basic User.");
      }
    } catch (error) {
      toast.error("Failed to update Basic User.");
      console.error("Error updating Basic User:", error);
    }
  };

  const columns = [
    { name: "Sl#", selector: (row) => row.sln, sortable: true, width: "70px" },
    { name: "Full Name", selector: (row) => row.fullName, sortable: true },
    { name: "Email", selector: (row) => row.email, wrap: true },
    { name: "Mobile#", selector: (row) => row.mobileNo, sortable: true },
    // { name: "City", selector: (row) => row.cityName },
    { name: "Address", selector: (row) => row.address, wrap: true },
    { name: "Company Name", selector: (row) => row.companyName },
    {
      name: "Work Experience",
      selector: (row) => row.workExpYear,
      sortable: true,
      center: true,
    },
    {
      name: "Action",
      selector: (row) => row.action,
      sortable: true,
      style: { textAlign: "center" },
    },
  ];

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Manage Basic User Data", 14, 20);

    const tableColumn = [
      "Sl#",
      "Full Name",
      "Email",
      "Mobile#",
      "City",
      "Address",
      "Company Name",
      "Work Experience",
    ];

    const tableRows = allData.map((row) => [
      row.sln,
      row.fullName,
      row.email,
      row.mobileNo,
      row.cityName,
      row.address,
      row.companyName,
      row.workExpYear,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("manage-basic-users.pdf");
  };

  const downloadExcel = () => {
    const worksheetData = allData.map((row) => ({
      "Sl#": row.sln,
      "Full Name": row.fullName,
      Email: row.email,
      "Mobile#": row.mobileNo,
      City: row.cityName,
      Address: row.address,
      "Company Name": row.companyName,
      "Work Experience": row.workExpYear,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Basic Users");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "manage-basic-users.xlsx");
  };

  const filter = () => {
    const text = filterText.toLowerCase();
    const filteredData = allData.filter((item) => {
      const nameMatch = item?.fullName?.toLowerCase().includes(text);
      const emailMatch = item?.email?.toLowerCase().includes(text);
      const companyMatch = item?.companyName?.toLowerCase().includes(text);
      const addressMatch = item?.address?.toLowerCase().includes(text);
      const mobileMatch = item?.mobileNo?.toString().includes(text);
      const cityMatch = item?.cityName?.toLowerCase().includes(text);

      return (
        nameMatch ||
        emailMatch ||
        companyMatch ||
        addressMatch ||
        mobileMatch ||
        cityMatch
      );
    });
    setFilteredInformation([...filteredData]);
  };

  useEffect(() => {
    filter();
  }, [filterText, allData]);

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid" id="edit-section-basic">
        <div className="mt-5">
          <h2 className="text-center" style={headLineStyle}>
            Manage Basic User
          </h2>
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              gap: "15px",
              justifyContent: "flex-end",
            }}
          >
            <button
              style={{
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor: "#73fc03",
                fontWeight: "600",
                border: "none",
              }}
              onClick={downloadPDF}
            >
              Download PDF ({allData?.length})
            </button>
            <button
              style={{
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor: "#73fc03",
                fontWeight: "600",
                border: "none",
              }}
              onClick={downloadExcel}
            >
              Download Excel ({allData?.length})
            </button>
          </div>
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <input
              onChange={(e) => {
                setFilterText(e?.target?.value);
              }}
              value={filterText}
              placeholder="Type to Search"
              style={{
                border: "1px solid grey",
                height: "35px",
                width: "30%",
                borderRadius: "5px",
                padding: "0 5px",
              }}
            />
          </div>
          <section className="chartBox_area">
            {editID ? (
              <>
                <h3>View/Modify Basic User Data</h3>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div
                      className="col-md-6 mt-3"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                      }}
                    >
                      <label>
                        Full Name<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="fullName"
                        value={formData?.fullName}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter Full Name"
                        required
                      />
                    </div>

                    <div className="col-md-6 mt-3">
                      <label>
                        Mobile Number<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="mobileNo"
                        value={formData?.mobileNo || ""}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter mobile number"
                        maxLength={10}
                        required
                      />
                    </div>

                    <div
                      className="col-md-6 mt-3"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                      }}
                    >
                      <label>
                        Email Address<span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData?.email || ""}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter Email address"
                        required
                      />
                    </div>

                    <div className="col-md-6 mt-3">
                      <label>
                        Password<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="password"
                        value={formData?.password || ""}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter Password"
                        required
                      />
                    </div>

                    <div className="col-md-6 mt-3">
                      <label>
                        City<span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        name="city"
                        value={formData?.city || ""}
                        onChange={(e) => handleChange(e)}
                        required
                      >
                        <option value="" disabled>
                          ---Select---
                        </option>
                        {cities.map((city) => (
                          <option key={city._id} value={city._id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mt-3">
                      <label>Address</label>
                      <input
                        type="text"
                        className="form-control"
                        name="address"
                        value={formData?.address || ""}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter address"
                      />
                    </div>

                    <div className="col-md-6 mt-3">
                      <label>Company Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="companyName"
                        value={formData?.companyName || ""}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter Company Name"
                      />
                    </div>

                    <div className="col-md-6 mt-3">
                      <label>Work Experience</label>
                      <input
                        type="text"
                        className="form-control"
                        name="workExpYear"
                        value={formData?.workExpYear || ""}
                        onChange={(e) => handleChange(e)}
                        placeholder="Enter Work Experience"
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary mt-3">
                    Save Changes
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    style={{ marginLeft: "11px" }}
                    onClick={() => {
                      setEditID(null);
                      fetchAllData();
                    }}
                  >
                    Cancel
                  </button>
                </form>
              </>
            ) : (
              <DataTable
                columns={columns}
                data={filterText ? filteredInformation : allData}
                pagination
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ManageBasicUser;
